import { ButtonHTMLAttributes, ReactNode, ElementType, forwardRef } from 'react'
import { motion } from 'framer-motion'
import { LucideIcon } from 'lucide-react'

interface ButtonBaseProps {
  variant?: 'primary' | 'secondary' | 'ghost' | 'danger' | 'success'
  size?: 'sm' | 'md' | 'lg'
  icon?: LucideIcon
  iconPosition?: 'left' | 'right'
  iconClassName?: string
  loading?: boolean
  fullWidth?: boolean
  children: ReactNode
  className?: string
  disabled?: boolean
}

interface ButtonProps extends ButtonBaseProps, Omit<ButtonHTMLAttributes<HTMLButtonElement>, 'children'> {
  as?: ElementType
  to?: string
}

const Button = forwardRef<HTMLButtonElement, ButtonProps>(function Button({
  variant = 'primary',
  size = 'md',
  icon: Icon,
  iconPosition = 'left',
  iconClassName = '',
  loading = false,
  fullWidth = false,
  children,
  className = '',
  disabled,
  as: Component = 'button',
  ...props
}, ref) {
  const baseClasses = 'inline-flex items-center justify-center font-medium rounded-lg transition-theme focus:outline-none focus:ring-2 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed'
  
  const variantClasses = {
    primary: 'bg-primary text-white hover:bg-primary/90 focus:ring-primary',
    secondary: 'bg-surface text-text border border-border hover:bg-surface/80 focus:ring-primary',
    ghost: 'bg-transparent text-text hover:bg-surface/50 focus:ring-primary',
    danger: 'bg-danger text-white hover:bg-danger/90 focus:ring-danger',
    success: 'bg-success text-white hover:bg-success/90 focus:ring-success'
  }
  
  const sizeClasses = {
    sm: 'px-3 py-1.5 text-sm',
    md: 'px-4 py-2 text-base',
    lg: 'px-6 py-3 text-lg'
  }
  
  const widthClass = fullWidth ? 'w-full' : ''
  
  const classes = `${baseClasses} ${variantClasses[variant]} ${sizeClasses[size]} ${widthClass} ${className}`
  
  const MotionComponent = motion(Component as any)
  
  const buttonContent = loading ? (
    <div className="flex items-center justify-center">
      <div className="w-4 h-4 border-2 border-current border-t-transparent rounded-full animate-spin mr-2" />
      <span>Loading...</span>
    </div>
  ) : (
    <div className="flex items-center justify-center gap-2">
      {Icon && iconPosition === 'left' && <Icon className={`w-4 h-4 ${iconClassName}`} />}
      <span>{children}</span>
      {Icon && iconPosition === 'right' && <Icon className={`w-4 h-4 ${iconClassName}`} />}
    </div>
  )
  
  return (
    <MotionComponent
      ref={ref}
      className={classes}
      disabled={disabled || loading}
      whileHover={disabled || loading ? undefined : { scale: 1.02 }}
      whileTap={disabled || loading ? undefined : { scale: 0.98 }}
      {...props}
    >
      {buttonContent}
    </MotionComponent>
  )
})

export default Button